import { X, Share, PlusSquare, Smartphone, Check, Download } from 'lucide-react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';

export default function PWAInstallModal({ isOpen, onClose, isIOS }) {
  const { isDark } = useTraderTheme();
  const { language } = useTraderLanguage();
  const isTr = language === 'tr';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn">
      <div 
        className={`w-full max-w-sm rounded-2xl p-6 shadow-2xl border relative ${
          isDark 
            ? 'bg-[#0f1724] border-gray-800 text-white' 
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-1 rounded-full ${
            isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
          }`}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Icon & Title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-cyan-500 to-emerald-400 p-0.5 flex items-center justify-center shadow-lg">
            <img src="/apple-touch-icon.png" alt="Webtera" className="w-full h-full rounded-[10px] object-cover" />
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Webtera Trader</h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {isTr ? 'Uygulama Çekmecesine Ekle' : 'Add to App Tray & Home Screen'}
            </p>
          </div>
        </div>

        {isIOS ? (
          /* iOS Safari Guide */
          <div className="space-y-4 text-sm mt-3">
            <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {isTr 
                ? 'Webtera Trader uygulamasını iPhone/iPad ana ekranınıza ve uygulama çekmecesine eklemek için Safari üzerinden aşağıdaki adımları uygulayın:' 
                : 'To install Webtera Trader on your iPhone/iPad Home Screen and App Library, follow these quick steps in Safari:'}
            </p>

            <div className={`space-y-3 p-3 rounded-xl ${isDark ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div className="text-xs">
                  <span className="font-semibold">{isTr ? 'Paylaş' : 'Share'}</span> {isTr ? 'düğmesine dokunun' : 'button in Safari'}
                  <div className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-gray-700/50 text-cyan-400 font-mono text-[10px]">
                    <Share size={12} /> {isTr ? 'Paylaş' : 'Share'}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div className="text-xs">
                  {isTr ? 'Aşağı kaydırın ve ' : 'Scroll down and tap '}
                  <span className="font-semibold text-cyan-400 inline-flex items-center gap-1">
                    <PlusSquare size={12} /> {isTr ? 'Ana Ekrana Ekle' : 'Add to Home Screen'}
                  </span>
                  {isTr ? ' seçeneğine dokunun.' : '.'}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div className="text-xs">
                  {isTr ? 'Sağ üstteki ' : 'Tap '}
                  <span className="font-semibold text-emerald-400">{isTr ? 'Ekle' : 'Add'}</span>
                  {isTr ? ' butonuna dokunun. Uygulama hemen cihazınıza yüklenir.' : ' in the top right corner. The app will be ready instantly.'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Android / Desktop Chrome / Edge Guide */
          <div className="space-y-4 text-sm mt-3">
            <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {isTr 
                ? 'Webtera Trader uygulamasını cihazınıza tam ekran bağımsız uygulama olarak yükleyebilirsiniz:'
                : 'You can install Webtera Trader as a full-screen standalone app on your device:'}
            </p>

            <div className={`space-y-3 p-3 rounded-xl ${isDark ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-xs shrink-0">
                  <Check size={14} />
                </div>
                <div className="text-xs">
                  {isTr ? 'Tarayıcı menüsünü (⋮) açın ve ' : 'Open browser menu (⋮) and tap '}
                  <strong className="text-cyan-400">{isTr ? 'Uygulamayı Yükle' : 'Install app'}</strong>
                  {isTr ? ' veya ' : ' or '}
                  <strong className="text-cyan-400">{isTr ? 'Ana Ekrana Ekle' : 'Add to Home Screen'}</strong>.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-xs shrink-0">
                  <Smartphone size={14} />
                </div>
                <div className="text-xs">
                  {isTr 
                    ? 'Uygulama çekmecesinde (App Tray) ve Ana Ekranınızda yerini alır.' 
                    : 'Appears directly in your App Tray / App Drawer & Home Screen.'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium text-sm transition-all shadow-lg shadow-cyan-500/20"
          >
            {isTr ? 'Anladım' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}
